/** Agent API — Faz 1.4 createApi factory ile yeniden yazıldı. Önce. */
import {createApi} from './_factory'
import type {AgentNs} from '@shared/types'

const api = createApi('agent')

const unwrap = <T>(response: T | {data: T}): T => {
  if (response && typeof response === 'object' && 'data' in (response as object)) {
    return (response as {data: T}).data
  }
  return response as T
}

export const AgentApi = {
  /** Hybrid chat endpoint. - Without `files`: standard JSON post. - With `files`. */
  chat: (data: AgentNs.AgentChatRequest, files: File[] = []) => {
    if (files.length === 0) {
      return api.post<AgentNs.AgentChatRequest, AgentNs.AgentChatApiResponse>('chat', data).then(unwrap);
    }
    const form = new FormData();
    form.append('payload', new Blob([JSON.stringify(data)], {type: 'application/json'}));
    for (const file of files) {
      form.append('files', file, file.name);
    }
    return api.upload<AgentNs.AgentChatApiResponse>('chat', form).then(unwrap);
  },

  /**
   * Held-document decision — resolves a `document_decision` card emitted by an
   * ANALYZE `analyze_document` turn. ACCEPT returns the ingestion chat turn in
   * `chat`; REJECT returns `chat: null` and a confirmation `message`.
   */
  decideDocument: (data: AgentNs.DocumentDecisionRequest) =>
    api.post<AgentNs.DocumentDecisionRequest, AgentNs.DocumentDecisionResponse>('document-decision', data).then(unwrap),

  /** Structure Generator — yapı/iyileştirme/soru üretimi. */
  generateStructure: (data: AgentNs.AgentStructureRequest) =>
    api.post<AgentNs.AgentStructureRequest, AgentNs.Structure>('structure', data).then(unwrap),

  /** Tool-call test endpoint'i (LLM tool karar logu). */
  toolTest: (data: AgentNs.ToolTestRequest) =>
    api.post<AgentNs.ToolTestRequest, AgentNs.ToolTestResponse>('tool-test', data).then(unwrap),

  /** Chat ekranı açılışında tek call'da: projectState + son N mesaj + qaSnapshot. */
  bootstrap: (data: AgentNs.ChatBootstrapRequest) =>
    api.post<AgentNs.ChatBootstrapRequest, AgentNs.ChatBootstrapResponse>('chat/bootstrap', data).then(unwrap),

  /** Cursor-based "daha eski mesajları yükle". */
  listMessages: (data: AgentNs.ChatListMessagesRequest) =>
    api.post<AgentNs.ChatListMessagesRequest, AgentNs.ChatMessagesPage>('chat/messages', data).then(unwrap),

  /** Reset chat — kullanıcının bu projedeki kendi chat history'sini siler. */
  resetChat: (data: AgentNs.ChatResetRequest) =>
    api.post<AgentNs.ChatResetRequest, AgentNs.ChatResetResponse>('chat/reset', data).then(unwrap),

  /** LLM audit (debug) listesi. */
  listLlmAudit: (data: AgentNs.LlmAuditListRequest) =>
    api.post<AgentNs.LlmAuditListRequest, AgentNs.LlmAuditPage>('llm-audit/list', data).then(unwrap),

  /** Tek bir audit kaydının ham detayı (system_inst, user_message, recent_turns vs). */
  getLlmAuditDetail: (data: AgentNs.LlmAuditDetailRequest) =>
    api.post<AgentNs.LlmAuditDetailRequest, AgentNs.LlmAuditDetail>('llm-audit/detail', data).then(unwrap),

  /** BUILDER preview /insert akışının ikinci adımı. */
  commitInsertStructure: (data: AgentStructureNs.CommitInsertRequest) =>
    api.post<AgentStructureNs.CommitInsertRequest, AgentStructureNs.CommitInsertResponse>(
      'structure/commit-insert', data,
    ).then(unwrap),
}

export namespace AgentStructureNs {
  export interface IdMappingEntry {
    previewId: string;
    backendId: string;
    kind?: 'module' | 'feature';
  }

  export interface CommitInsertRequest {
    workspaceId: string;
    projectId: string;
    /** Tam Yol 1 — kullanıcının onayladığı yapı taslağı (modules[].features[]. */
    structure?: Record<string, unknown>;
    /** Legacy: frontend add-groups-with-features ile önceden yarattıysa… */
    idMapping?: IdMappingEntry[];
    insertedAt?: string;
    /** Frontend insert bubble metni (örn. "Structure inserted into project."). */
    userMessageText?: string;
  }

  export interface CommitInsertResponse {
    success: boolean;
    insertedAt: string;
    unmatchedCount: number;
    /** Tam Yol 1 — backend'in delta-apply sırasında yarattığı yeni item'ların… */
    idMapping?: IdMappingEntry[];
  }
}
